import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '../../components/Modal';
import Button from '../../components/ui/Button';
import Pagination from '../../components/ui/Pagination';
import { EmptyState, ErrorState, LoadingIndicator } from '../../components/ui/States';
import { describeApiError, type DialogContent } from '../../utils/errorPresentation';
import { formatDateTime } from '../../utils/dateFormat';
import CatalogueItemForm from '../catalogue/CatalogueItemForm';
import type { CreateCatalogueItemPayload, MasterCatalogueItem } from '../../lib/masterCatalogueApi';
import {
  decideCatalogueSubmission,
  listCatalogueSubmissions,
  notifyReviewQueueChanged,
  type CatalogueSubmission,
  type CatalogueSubmissionActor,
  type DecideCatalogueSubmissionBody,
  type DecideCatalogueSubmissionResponse,
} from '../../lib/catalogueSubmissionsApi';

/**
 * The catalogue-addition review queue's pending list, one card per submission.
 *
 * Three actions: Approve as submitted, Edit and approve (the same
 * `CatalogueItemForm` the Master Catalogue page uses, via its
 * `onSubmitOverride`), and Reject with a required reason. `likelyMatches` is
 * shown on every card — how an admin catches a near-duplicate before it becomes
 * a second catalogue row for a product already there.
 *
 * This is a queue two admins can have open at once: a 409
 * `CATALOGUE_SUBMISSION_ALREADY_DECIDED` is handled by CODE, never by matching
 * the server's prose, and reported plainly with the list refreshed under it.
 */

const PAGE_SIZE = 20;

function actorLabel(actor: CatalogueSubmissionActor | null | undefined): string {
  if (!actor) return 'Unknown';
  if (typeof actor === 'string') return actor;
  const name = [actor.firstName, actor.lastName].filter(Boolean).join(' ').trim();
  return actor.name || name || actor.email || actor._id || 'Unknown';
}

function formatMoney(amount: number | null | undefined, currency?: string | null): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  const prefix = currency ? `${currency} ` : '';
  return `${prefix}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** An approval whose quotation line was not linked for a reason someone should know about. */
function approvalNotice(name: string, data: DecideCatalogueSubmissionResponse['data'] | undefined): string {
  if (data?.quotationLineLinkOutcome === 'not_linked') {
    return `"${name}" approved, but its quotation line was not linked to the catalogue; use ↻ refresh on that line.`;
  }
  return `"${name}" approved.`;
}

export interface CatalogueSubmissionsListProps {
  /** Reports the pending total up to the Review page heading and the sidebar badge, whenever it changes. */
  onTotalChange?: (total: number) => void;
}

export default function CatalogueSubmissionsList({ onTotalChange }: CatalogueSubmissionsListProps) {
  const [submissions, setSubmissions] = useState<CatalogueSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<DialogContent | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, DialogContent>>({});
  const [rejectOpenId, setRejectOpenId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(
    async (p: number) => {
      setLoading(true);
      setError(null);
      try {
        const res = await listCatalogueSubmissions({ status: 'pending', page: p, limit: PAGE_SIZE });
        setSubmissions(res.data?.submissions || []);
        const nextTotal = res.data?.total ?? 0;
        setTotal(nextTotal);
        onTotalChange?.(nextTotal);
      } catch (e: any) {
        setError(
          describeApiError(e, {
            action: 'Load catalogue additions',
            fallback: 'Could not load the catalogue additions queue.',
          })
        );
      } finally {
        setLoading(false);
      }
    },
    [onTotalChange]
  );

  useEffect(() => {
    load(page);
  }, [page, load]);

  const editingSubmission = editingId ? submissions.find((s) => s._id === editingId) ?? null : null;

  const clearRowError = (id: string) =>
    setRowErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });

  async function decide(submission: CatalogueSubmission, body: DecideCatalogueSubmissionBody) {
    setBusyId(submission._id);
    clearRowError(submission._id);
    setNotice(null);
    try {
      const res = await decideCatalogueSubmission(submission._id, body);
      setNotice(
        body.decision === 'approve'
          ? approvalNotice(submission.payload.item_name, res?.data)
          : `"${submission.payload.item_name}" rejected.`
      );
      notifyReviewQueueChanged();
      setRejectOpenId(null);
      setRejectReason('');
      await load(page);
    } catch (e: any) {
      if (e?.code === 'CATALOGUE_SUBMISSION_ALREADY_DECIDED') {
        // A queue two admins can have open at once — somebody else got there first.
        setNotice('Another admin already decided this one. The list has been refreshed.');
        notifyReviewQueueChanged();
        setRejectOpenId(null);
        setRejectReason('');
        await load(page);
      } else {
        setRowErrors((prev) => ({
          ...prev,
          [submission._id]: describeApiError(e, {
            action: body.decision === 'approve' ? 'Approve item' : 'Reject item',
            fallback: 'Could not save that decision.',
          }),
        }));
      }
    } finally {
      setBusyId(null);
    }
  }

  const approve = (submission: CatalogueSubmission) => decide(submission, { decision: 'approve' });

  const confirmReject = (submission: CatalogueSubmission) => {
    const reason = rejectReason.trim();
    if (!reason) return;
    decide(submission, { decision: 'reject', reason });
  };

  /**
   * The edit-then-approve path: `CatalogueItemForm`'s own Save, redirected to
   * the decision endpoint with this payload as `payloadOverride`, instead of
   * `createMasterCatalogueItem`. An ALREADY_DECIDED 409 here is swallowed
   * rather than shown as a form refusal — the honest outcome is "someone else
   * already decided this, so there is nothing left to edit", not a validation
   * message on a field the person just fixed.
   */
  const onSubmitOverride =
    (submission: CatalogueSubmission) =>
    async (payload: CreateCatalogueItemPayload): Promise<MasterCatalogueItem | null> => {
      try {
        const res = await decideCatalogueSubmission(submission._id, { decision: 'approve', payloadOverride: payload });
        setNotice(approvalNotice(payload.item_name, res?.data));
        notifyReviewQueueChanged();
        return null;
      } catch (e: any) {
        if (e?.code === 'CATALOGUE_SUBMISSION_ALREADY_DECIDED') {
          setNotice('Another admin already decided this one. The list has been refreshed.');
          notifyReviewQueueChanged();
          return null;
        }
        throw e;
      }
    };

  return (
    <div className="flex flex-col gap-3">
      {notice && (
        <div role="status" className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          {notice}
        </div>
      )}

      {error ? (
        <ErrorState message={error.summary || error.title} onRetry={() => load(page)} />
      ) : loading && submissions.length === 0 ? (
        <LoadingIndicator label="Loading catalogue additions…" />
      ) : submissions.length === 0 ? (
        <EmptyState
          title="Nothing waiting for review."
          detail="Every catalogue addition submitted from a quotation has been decided."
        />
      ) : (
        <div className="grid gap-3">
          {submissions.map((submission) => {
            const matches = submission.likelyMatches || [];
            const busy = busyId === submission._id;
            const rowError = rowErrors[submission._id];
            return (
              <div key={submission._id} className="rounded-md border border-slate-200 p-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{submission.payload.item_name}</p>
                  <p className="mt-0.5 text-xs text-slate-600">
                    {formatMoney(submission.payload.item_price, submission.payload.currency)}
                    {submission.payload.unit ? ` per ${submission.payload.unit}` : ''}
                    {submission.payload.supplier ? ` · ${submission.payload.supplier}` : ''}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Submitted by {actorLabel(submission.submittedBy)}
                    {submission.submittedAt ? ` on ${formatDateTime(submission.submittedAt)}` : ''}
                  </p>
                  {submission.requestedFrom?.quotationNumber && (
                    <p className="mt-0.5 text-xs text-slate-500">
                      From quotation {submission.requestedFrom.quotationNumber}
                      {submission.requestedFrom.lineItemName
                        ? `, line "${submission.requestedFrom.lineItemName}"`
                        : ''}
                    </p>
                  )}
                </div>

                <div className="mt-2">
                  <p className="text-xs font-medium text-slate-700">Possible existing matches</p>
                  {matches.length > 0 ? (
                    <ul className="mt-1 grid gap-1">
                      {matches.map((m) => (
                        <li
                          key={m._id}
                          className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900"
                        >
                          <span className="font-medium">{m.item_name}</span>
                          {m.unit ? ` — ${m.unit}` : ''}
                          {m.supplier ? ` · ${m.supplier}` : ''}
                          {` · ${formatMoney(m.item_price, m.currency)}`}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1 text-xs text-slate-500">No similar items found in the Master Catalogue.</p>
                  )}
                </div>

                {rowError && (
                  <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                    <div className="font-semibold">{rowError.title}</div>
                    {rowError.summary && <div className="mt-0.5">{rowError.summary}</div>}
                  </div>
                )}

                {rejectOpenId === submission._id ? (
                  <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 p-2">
                    <label
                      htmlFor={`reject-reason-${submission._id}`}
                      className="block text-xs font-medium text-rose-800 mb-1"
                    >
                      Why is this rejected? Required.
                    </label>
                    <textarea
                      id={`reject-reason-${submission._id}`}
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={2}
                      className="w-full rounded border border-rose-300 px-2 py-1 text-xs"
                      placeholder="e.g. Already in the catalogue as ..."
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => {
                          setRejectOpenId(null);
                          setRejectReason('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={!rejectReason.trim()}
                        loading={busy}
                        onClick={() => confirmReject(submission)}
                      >
                        Confirm reject
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" variant="primary" loading={busy} onClick={() => approve(submission)}>
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => setEditingId(submission._id)}
                    >
                      Edit and approve
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy}
                      onClick={() => {
                        setNotice(null);
                        setRejectReason('');
                        setRejectOpenId(submission._id);
                      }}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} noun="catalogue additions" />

      {editingSubmission && (
        <Modal
          isOpen
          title="Edit and approve"
          onClose={() => setEditingId(null)}
          maxWidthClass="max-w-md"
        >
          <CatalogueItemForm
            key={editingSubmission._id}
            mode={{ kind: 'create' }}
            seed={editingSubmission.payload}
            submitLabel="Approve"
            banner={
              <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Editing this catalogue addition before approving it.
                {editingSubmission.requestedFrom?.quotationNumber && (
                  <>
                    {' '}
                    From quotation {editingSubmission.requestedFrom.quotationNumber}
                    {editingSubmission.requestedFrom.lineItemName
                      ? `, line "${editingSubmission.requestedFrom.lineItemName}"`
                      : ''}
                    .
                  </>
                )}
                {editingSubmission.likelyMatches && editingSubmission.likelyMatches.length > 0 && (
                  <>
                    {' '}
                    {editingSubmission.likelyMatches.length} possible existing match
                    {editingSubmission.likelyMatches.length === 1 ? '' : 'es'} listed below — check before approving a
                    duplicate.
                  </>
                )}
              </div>
            }
            onCancel={() => setEditingId(null)}
            onSaved={() => {
              setEditingId(null);
              load(page);
            }}
            onSubmitOverride={onSubmitOverride(editingSubmission)}
          />
        </Modal>
      )}
    </div>
  );
}
